/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useSwiper} from 'swiper/react';

import {Box, useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {VIDEO_OPTIONS} from '@amblique/base/app/constants/video-options';
import {usePluginOptions} from '@amblique/base/app/hooks/use-plugin-options';
import {useResponsiveValue} from '@amblique/base/app/hooks/use-responsive-value';
import {useVideoAutopilot} from '@amblique/base/app/hooks/use-video-autopilot';
import {useVideoPlayer} from '@amblique/base/app/hooks/use-video-player';

/**
 * Video Component
 *
 * Plays Vimeo and YouTube videos with advanced features:
 * - Server-side configuration via site preferences
 * - Responsive video IDs (responsive value format)
 * - Breakpoint-based options
 * - Autopilot (visibility-based play/pause)
 * - Imperative API via ref
 *
 * @param {{
 *   id: string,
 *   presets?: string | string[],
 *   options?: object | string,
 *   ratio?: number,
 *   clip?: number | string | array,
 *   size?: 'cover' | 'contain' | 'fill',
 *   readonly?: boolean,
 *   autopilot?: boolean | string | object,
 *   className?: string,
 * }} props
 *
 * @example Basic usage
 * <Video id="16401445" />
 *
 * @example With presets
 * <Video id="16401445" presets="hero" />
 *
 * @example Readonly mode (auto-enables autopilot for visibility-based playback)
 * <Video id="16401445" readonly />
 *
 * @example With autopilot (auto-applies autopilot preset)
 * <Video id="16401445" autopilot />
 * <Video id="16401445" autopilot="resume" />
 *
 * @example With responsive video IDs (CSS property syntax)
 * <Video id="mobile: mobile-id; tablet: tablet-id; desktop: desktop-id" />
 *
 * @example With responsive video IDs (shorthand syntax)
 * <Video id="mobile-id, tablet-id, desktop-id" />
 *
 * @example With inline options
 * <Video
 *   id="16401445"
 *   presets="readonly"
 *   options={{ loop: false, controls: true }}
 * />
 *
 * @example With ref for imperative control
 * const videoRef = useRef();
 * <Video ref={videoRef} id="16401445" />
 * // Later: videoRef.current.play()
 */
export const Video = forwardRef(({
  id,
  presets,
  options,
  ratio,
  clip,
  size,
  readonly,
  autopilot: autopilotProp,
  className,
  ...props
}, ref) => {
  const styles = useStyleConfig('Video');
  const swiper = useSwiper();

  // Merge inline props into options
  const {userPresets, userOptions} = useMemo(() => {
    const userPresets = [...(Array.isArray(presets) ? presets : typeof presets === 'string' ? [presets] : [])];
    const userOptions = {
      ...options,
      ...(ratio !== undefined && {ratio}),
      ...(clip !== undefined && {clip}),
      ...(size !== undefined && {size}),
      ...(readonly !== undefined && {readonly}),
      ...(autopilotProp !== undefined && {autopilot: autopilotProp}),
    };

    // Handle readonly mode:
    // - If readonly is true and autopilot is not explicitly set, force autopilot to true
    // - This ensures readonly videos can be controlled via intersection observer
    // - Without autopilot, readonly videos have no way to start playback (no controls)
    if (userOptions.readonly && userOptions.autopilot === undefined) {
      userOptions.autopilot = true;
    }

    return {userOptions, userPresets};
  }, [options, ratio, clip, size, readonly, autopilotProp, presets]);

  const readonlyOptions = usePluginOptions('video', VIDEO_OPTIONS, 'readonly');
  const autopilotOptions = usePluginOptions('video', VIDEO_OPTIONS, 'autopilot');

  // Post-process callback: Merge readonly/autopilot options
  // Note: resolvedOptions now includes responsive overrides already applied
  const postProcess = useCallback((options) => {
    if (options.readonly) {
      Object.assign(options, readonlyOptions);
    }

    if (options.autopilot) {
      Object.assign(options, autopilotOptions);
    }

    return options;
  }, [readonlyOptions, autopilotOptions]);

  // Resolve options (default + server + presets + inline + responsive)
  // Note: usePluginOptions now handles responsive options internally
  const responsiveOptions = usePluginOptions('video', VIDEO_OPTIONS, userPresets, userOptions, postProcess);
  const currentVideoId = useResponsiveValue(id, 'value');
  const {containerRef, video, init} = useVideoPlayer(currentVideoId, responsiveOptions);

  useVideoAutopilot({
    elementRef: containerRef,
    video,
    options: responsiveOptions,
    init,
  });

  // Expose imperative API
  useImperativeHandle(ref, () => video, [video]);

  const isReadonly = responsiveOptions.readonly || readonly;
  const classNames = ['amb-video', isReadonly && 'readonly', className].filter(Boolean).join(' ');

  useEffect(() => {
    swiper?.updateAutoHeight();
  }, []);

  return (
    <Box
      ref={containerRef}
      aspectRatio={responsiveOptions.ratio || 16 / 9}
      className={classNames}
      {...styles}
      {...props}
    />
  );
});

Video.displayName = 'Video';

Video.propTypes = {
  // Required
  id: PropTypes.string.isRequired,

  // Presets
  presets: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),

  // Inline options (object or JSON string)
  options: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string
  ]),

  // Quick overrides (same as options.*)
  ratio: PropTypes.number,
  clip: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array
  ]),
  size: PropTypes.oneOf(['cover', 'contain', 'fill']),
  readonly: PropTypes.bool,
  autopilot: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.object
  ]),

  // Styling
  className: PropTypes.string,
};

export default Video;
